import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FolderKanban, Plus, X } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { getCustomerService } from '@/services/customers'
import { returnCasesHttp } from '@/services/returnCases'
import { toast } from '@/store/toastStore'
import type { PreparationLifecycle, ReturnPreparationCase } from '@/types/returnCase.types'

interface Props {
  customerId?: string
}
export default function ReturnCasePortfolioPage({ customerId: linkedCustomerId }: Props = {}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()
  const canCreate = hasPermission('txh.assessment.case.create')
  const [customerId, setCustomerId] = useState(
    linkedCustomerId ?? searchParams.get('customerId') ?? ''
  )
  const [periodValue, setPeriodValue] = useState('2026-27')
  const [lifecycle, setLifecycle] = useState<PreparationLifecycle | ''>('')
  const [selectedCase, setSelectedCase] = useState<ReturnPreparationCase | null>(null)
  const customersQuery = useQuery({
    queryKey: ['return-case-customers'],
    queryFn: () => getCustomerService().list(),
  })
  const customers = customersQuery.data?.filter(customer => customer.status !== 'archived') ?? []
  const selectedCustomer = customers.find(customer => String(customer.id) === customerId)
  const query = useQuery({
    queryKey: ['return-cases', lifecycle, linkedCustomerId],
    queryFn: () =>
      returnCasesHttp.list({
        lifecycleState: lifecycle || undefined,
        customerId: linkedCustomerId,
      }),
  })
  const createMutation = useMutation({
    mutationFn: () => {
      if (!selectedCustomer) throw new Error('Select an accessible customer')
      return returnCasesHttp.create({ customer_id: customerId, period_value: periodValue.trim() })
    },
    onSuccess: async created => {
      await queryClient.invalidateQueries({ queryKey: ['return-cases'] })
      toast.success('V2 return case created', `Case ${created.period_value} is ready for setup.`)
      navigate(`/return-preparation-cases/${created.id}/workspace`)
    },
    onError: error =>
      toast.error(
        'Could not create case',
        error instanceof Error ? error.message : 'Please try again.'
      ),
  })
  const nameFor = (id: string) =>
    customers.find(customer => String(customer.id) === String(id))?.fullName ?? `Customer ${id}`
  return (
    <div className="space-y-6 text-text-primary">
      {!linkedCustomerId && (
        <header className="flex items-start gap-3">
          <div className="rounded-lg bg-primary-100 p-3 dark:bg-primary-900/30">
            <FolderKanban className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">
              Return Preparation Cases <span className="badge badge-primary">V2</span>
            </h1>
            <p className="text-sm text-text-secondary">
              Preparation-only cases; legacy assessments remain separate and unchanged.
            </p>
          </div>
        </header>
      )}
      {canCreate && (
        <section className="card card-elevated p-6 space-y-4" aria-label="Create V2 case">
          <h2 className="text-lg font-semibold">Start an assessment</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="text-sm font-medium">
              Customer
              <select
                className="input input-bordered mt-1 w-full"
                value={customerId}
                onChange={event => setCustomerId(event.target.value)}
                disabled={Boolean(linkedCustomerId) || customersQuery.isLoading}
              >
                <option value="">Select a customer</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.fullName} · {customer.pan}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium">
              Assessment year
              <input
                className="input input-bordered mt-1 w-full"
                value={periodValue}
                onChange={event => setPeriodValue(event.target.value)}
                placeholder="2026-27"
              />
            </label>
          </div>
          <p className="text-sm text-text-secondary">
            Only customers within your assigned portfolio are listed.
          </p>
          {customersQuery.isError && (
            <p role="alert" className="text-red-600 dark:text-red-400">
              Customers could not be loaded.{' '}
              <button className="btn btn-ghost btn-sm" onClick={() => customersQuery.refetch()}>
                Retry
              </button>
            </p>
          )}
          {selectedCustomer && (
            <dl
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg bg-surface-overlay p-4 text-sm"
              aria-label="Customer information"
            >
              {[
                ['Name', selectedCustomer.fullName],
                ['PAN', selectedCustomer.pan],
                ['Email', selectedCustomer.emailPrimary],
                ['Mobile', selectedCustomer.mobilePrimary],
                ['Aadhaar', selectedCustomer.aadhaar ?? 'Not provided'],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-text-secondary">{label}</dt>
                  <dd className="break-words font-medium">{value}</dd>
                </div>
              ))}
            </dl>
          )}
          <button
            className="btn btn-primary flex items-center gap-2"
            disabled={
              !selectedCustomer || !/^20\d{2}-\d{2}$/.test(periodValue) || createMutation.isPending
            }
            onClick={() => createMutation.mutate()}
          >
            <Plus className="h-4 w-4" />
            Create case
          </button>
        </section>
      )}
      <section className="card card-elevated p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">V2 portfolio</h2>
          <select
            className="input input-bordered"
            value={lifecycle}
            onChange={event => setLifecycle(event.target.value as PreparationLifecycle | '')}
            aria-label="Lifecycle filter"
          >
            <option value="">All lifecycle states</option>
            {['SETUP', 'PREPARATION', 'REVIEW', 'READINESS'].map(value => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        {query.isLoading && <p className="text-text-secondary">Loading return cases…</p>}
        {query.isError && (
          <div role="alert" className="text-red-600 dark:text-red-400">
            The V2 portfolio could not be loaded. Your legacy assessments are unaffected.{' '}
            <button className="btn btn-ghost btn-sm" onClick={() => query.refetch()}>
              Retry
            </button>
          </div>
        )}
        {!query.isLoading && !query.isError && query.data?.items.length === 0 && (
          <div className="py-12 text-center text-text-secondary">
            No V2 return-preparation cases match this view.
          </div>
        )}
        <div className="space-y-3">
          {query.data?.items.map(item => (
            <div key={item.id} className="rounded-lg border border-surface-overlay p-4">
              <div className="grid gap-3 md:grid-cols-4">
                <div>
                  <button
                    type="button"
                    onClick={() => navigate(`/return-preparation-cases/${item.id}/workspace`)}
                    className="btn btn-ghost text-primary-600 p-0"
                  >
                    {nameFor(item.customer_id)}
                  </button>
                  <p className="text-sm text-text-secondary">
                    {item.period_value} · {item.return_kind} · #{item.return_sequence}
                  </p>
                </div>
                {[
                  ['Lifecycle', item.lifecycle_state],
                  ['Work', item.work_state],
                  ['Review', item.review_state],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-sm text-text-secondary">{label}</p>
                    <p>{value.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setSelectedCase(item)}
                >
                  View details
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => navigate(`/return-preparation-cases/${item.id}/workspace`)}
                >
                  Open case
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
      {selectedCase && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="case-details-title"
          onKeyDown={event => {
            if (event.key === 'Escape') setSelectedCase(null)
          }}
        >
          <section className="bg-white dark:bg-surface-dark rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <header className="p-6 border-b border-surface-overlay flex items-center justify-between">
              <h2 id="case-details-title" className="text-xl font-semibold">
                {nameFor(selectedCase.customer_id)} · {selectedCase.period_value}
              </h2>
              <button
                type="button"
                className="btn btn-ghost btn-circle btn-sm"
                aria-label="Close case details"
                onClick={() => setSelectedCase(null)}
              >
                <X className="h-5 w-5" />
              </button>
            </header>
            <dl className="p-6 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {Object.entries(selectedCase)
                .filter(([key]) => key !== 'tenant_id')
                .map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-text-secondary capitalize">{key.replace(/_/g, ' ')}</dt>
                    <dd className="font-medium break-words">
                      {value == null
                        ? 'Not set'
                        : typeof value === 'boolean'
                          ? value
                            ? 'Yes'
                            : 'No'
                          : String(value).replace(/_/g, ' ')}
                    </dd>
                  </div>
                ))}
            </dl>
            <footer className="p-6 border-t border-surface-overlay">
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/return-preparation-cases/${selectedCase.id}/workspace`)}
              >
                Open case
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  )
}
